import { useState, useEffect, useContext } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButton from '../components/buttons/RoundedButton'
import SectionHeaderView from '../components/views/SectionHeaderView'
import TwoButtonsModal from '../components/modals/TwoButtonsModal'
import LoadingModal from '../components/modals/LoadingModal'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import arrow from '../assets/arrow.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const BoatDetails = () => {
    const { id } = useParams()

    const [usageTypes, setUsageTypes] = useState(null)
    const [harbors, setHarbors] = useState(null)
    const [boat, setBoat] = useState(null)

    const [showRemoveBoatModal, setShowRemoveBoatModal] = useState(false)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)
        
            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.CLIENT) {
                navigate('/')
            } else {
                getDropdowns()
                getBoatDetails()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get dropdowns request
    const getDropdowns = () => {
        fetch('/api/Boat/Dropdowns', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                setUsageTypes(jsonRes.usageTypes)
                setHarbors(jsonRes.harbors)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setUsageTypes(null)
                    setHarbors(null) 
                }, 1000)
            }
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setUsageTypes(null)
                setHarbors(null)
            }, 1000)
        })
    }

    // Handle get boat details
    const getBoatDetails = () => {
        fetch(`/api/Boat/${id}/Details`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
                  
                    setBoat(jsonRes)
                } else {
                    showErrorToast(strings.getBoatDetailsError)
                    setBoat(null)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setBoat(null)
            }, 1000) 
        })
    }

    // Show remove modal
    const onClickRemove = () => {
        setShowRemoveBoatModal(true)
    }

    // Handles the removal of a boat
    const removeBoat = () => {
        setShowRemoveBoatModal(false)
        setIsLoading(true)

        // Remove boat
        fetch(`/api/Boat/${boat.id}/Delete`, {
            method:'DELETE',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(() => {
                setIsLoading(false)

                if (response.status === 200) {
                    navigate('/boats')
                } else {
                    showErrorToast(strings.deleteBoatError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)  
        })
    }

    // Check the usage type and show matching text
    const showUsageTypeText = (usageTypeId) => {
        const usageType = usageTypes && usageTypes.find((usageType) => usageType.id === usageTypeId)
        return usageType && usageType.name
    }

    // Check the harbor and show matching text
    const showHarborText = (harborId) => {
        const harbor = harbors && harbors.find((harbor) => harbor.id === harborId)
        return harbor && harbor.name
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='px-[12.5rem] min-h-screen mb-6'>

                { isLoading ?
                    <LoadingModal />
                :   <>
                        <div className='flex items-center justify-between mt-[3.375rem] mb-[1.875rem]'>

                            <div className='flex flex-row items-center space-x-2'>
                                <Link to='/boats'>
                                    <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                                </Link>
                                <p className='font-montserrat font-semibold text-accent'>{strings.seeBoat}</p>
                            </div>

                            <div className='flex flex-row items-center space-x-6'>
                                <div className='flex w-[7.688rem]'>
                                    <RoundedButton
                                        onClick={() => navigate(`/edit-boat/${boat.id}`)}
                                        text={strings.update}
                                        height={40}
                                        bgColor='#007EB4'
                                    />
                                </div>

                                <div className='flex w-[7.688rem]'>
                                    <RoundedButton 
                                        onClick={onClickRemove}
                                        text={strings.remove}
                                        height={40}
                                        bgColor='#E85F5C'
                                    />
                                </div>
                                
                            </div>

                        </div>

                        <SectionHeaderView 
                            text={strings.info}
                            margin={24}
                        />

                        <div className='flex flex-row px-4 mb-6'>
                            <div className='flex flex-col w-1/4'>
                                <p className='font-montserrat text-sm'>{strings.boatName}</p>
                                <p className='font-montserrat text-xl font-semibold'>{boat && boat.name}</p>
                            </div>
                            <div className='flex flex-col w-1/4'>
                                <p className='font-montserrat text-sm'>{strings.registration}</p>
                                <p className='font-montserrat text-xl font-semibold'>{boat && boat.registration}</p>
                            </div>
                            <div className='flex flex-col w-1/4'>
                                <p className='font-montserrat text-sm'>{strings.grossTonnage}</p>
                                <p className='font-montserrat text-xl font-semibold'>{boat && boat.grossTonnage + strings.grt}</p>
                            </div>
                            <div className='flex flex-col w-1/4'>
                                <p className='font-montserrat text-sm'>{strings.outOutLength}</p>
                                <p className='font-montserrat text-xl font-semibold'>{boat && boat.length + strings.m}</p>
                            </div>
                        </div>

                        <div className='flex flex-row px-4 mb-[4.5rem]'>
                            <div className='flex flex-col w-1/4'>
                                <p className='font-montserrat text-sm'>{strings.typeOfUse}</p>
                                <p className='font-montserrat text-xl font-semibold'>{boat && showUsageTypeText(boat.usageTypeId)}</p>
                            </div>
                            <div className='flex flex-col w-1/4'>
                                <p className='font-montserrat text-sm'>{strings.affectedShipyard}</p>
                                <p className='font-montserrat text-xl font-semibold'>{boat && showHarborText(boat.harborId)}</p>
                            </div>
                        </div>
                    </>
                }

            </div>

            <TwoButtonsModal 
                open={showRemoveBoatModal}
                onClose={() => setShowRemoveBoatModal(false)}
                title={strings.removeBoat}
                message={strings.removeBoatMessage}
                buttonText1={strings.keep}
                buttonText2={strings.removeBoatTitle}
                onClick={removeBoat}
            />

            <ToastContainer />
        </>
    )
}

export default BoatDetails